/**
 * Hide obsolete rows from a catalog diagram when the catalog explicitly links
 * them to a newer part that is also present in the section.
 *
 * Catalogs use different labels for the same relationship. SAIC exposes
 * "Part/Usage Level New Part" while Xentry normalizes successor data to
 * `replaced_by`. We deliberately recognise only forward-looking labels:
 * `replaces` points backwards and must never make the current part disappear.
 */

type SupersessionRow = {
  partId?: unknown;
  partNumber?: unknown;
  part_number?: unknown;
  extra_details?: Record<string, unknown> | null;
};

const normalize = (value: unknown): string =>
  String(value ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

const isForwardReplacementKey = (key: string): boolean => {
  const normalized = key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  return normalized.endsWith('newpart')
    || normalized === 'replacedby'
    || normalized === 'supersededby'
    || normalized === 'successorpart'
    || normalized === 'successorparts';
};

const identities = (row: SupersessionRow): string[] =>
  Array.from(new Set([
    normalize(row.part_number),
    normalize(row.partNumber),
    normalize(row.partId),
  ].filter(Boolean)));

/**
 * The number the CATALOG gave the row. The backend moves an obsolete row onto
 * the current end of its chain (superseded_part_number remembers the original),
 * so `partNumber` alone would make such a row look like its own successor — and
 * a row that is its own successor can never be collapsed.
 */
const catalogIdentities = (row: SupersessionRow): string[] => {
  const original = normalize(row.extra_details?.superseded_part_number);
  return original ? [original] : identities(row);
};

const replacementIdentity = (row: SupersessionRow): string | null => {
  for (const [key, value] of Object.entries(row.extra_details || {})) {
    if (!isForwardReplacementKey(key)) continue;
    const replacement = normalize(value);
    if (replacement && !catalogIdentities(row).includes(replacement)) return replacement;
  }
  return null;
};

/**
 * The backend writes one normalized chain per part, whatever the catalog:
 * extra_details.supersession_chain = "OLD → MID → NEW" (siblings "A + B").
 * See catax_ai/catalogs/supersession.py.
 */
export const supersessionChain = (row: SupersessionRow): string[][] =>
  String(row.extra_details?.supersession_chain ?? '')
    .split('→')
    .map(level => level.split('+').map(s => s.trim()).filter(Boolean))
    .filter(level => level.length > 0);

const levelHasRow = (level: string[], row: SupersessionRow): boolean =>
  level.some(n => identities(row).includes(normalize(n)));

/** Catalog bookkeeping about a number's lineage — replaces / replaced by /
 *  supersession chain / logistics notes. True for any catalog; it never tells
 *  two candidates apart for the user, so it lives under Catalog details and is
 *  never promoted onto the row as a "difference". */
export function isSupersessionDetailKey(key: string): boolean {
  const n = String(key).replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  return n === 'replaces' || n === 'replacedby' || n === 'supersededby' || n === 'supersedes'
    || n === 'supersessionchain' || n === 'supersededpartnumber'
    || n.startsWith('successor') || n.startsWith('predecessor') || n.endsWith('newpart')
    || n.startsWith('logistic');
}

/** Every OTHER number in the row's replacement chain — search-bar haystack. */
export function supersessionNumbers(row: SupersessionRow): string[] {
  return supersessionChain(row).flat().filter(n => !identities(row).includes(normalize(n)));
}

/** "B → C" when the catalog replaced this row by newer number(s); null otherwise. */
export function supersededBy(row: SupersessionRow): string | null {
  const levels = supersessionChain(row);
  const own = levels.findIndex(level => levelHasRow(level, row));
  if (own < 0 || own === levels.length - 1) return null;
  return levels.slice(own + 1).map(level => level.join(' + ')).join(' → ');
}

/**
 * Collapse A -> B -> C to C. A row is hidden only when its forward pointer
 * resolves to another row in this payload, so incomplete catalog data never
 * makes a part vanish. Cycles are retained because they have no trustworthy
 * terminal replacement.
 */
export function collapseSupersededPartRows<T extends SupersessionRow>(rows: T[]): T[] {
  if (rows.length < 2) return rows;

  const indexByIdentity = new Map<string, number>();
  rows.forEach((row, index) => catalogIdentities(row).forEach(id => {
    if (!indexByIdentity.has(id)) indexByIdentity.set(id, index);
  }));

  const next = new Map<number, number>();
  rows.forEach((row, index) => {
    const replacement = replacementIdentity(row);
    const target = replacement ? indexByIdentity.get(replacement) : undefined;
    if (target != null && target !== index) next.set(index, target);
  });
  if (next.size === 0) return rows;

  const cycleMembers = new Set<number>();
  for (const start of Array.from(next.keys())) {
    const path: number[] = [];
    const position = new Map<number, number>();
    let current: number | undefined = start;
    while (current != null && next.has(current)) {
      const cycleStart = position.get(current);
      if (cycleStart != null) {
        path.slice(cycleStart).forEach(index => cycleMembers.add(index));
        break;
      }
      position.set(current, path.length);
      path.push(current);
      current = next.get(current);
    }
  }

  const collapsed = rows.filter((_, index) => !next.has(index) || cycleMembers.has(index));
  return collapsed.length === rows.length ? rows : collapsed;
}
